import styled from '@emotion/styled';
import { AnimatePresence, motion } from 'motion/react';
import { PropsWithChildren, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props extends PropsWithChildren {
  open: boolean;
  onClose?: () => void;
  variant?: 'default' | 'preview';
}

export default function Modal({ children, open, onClose, variant = 'default' }: Props) {
  const isPreview = variant === 'preview';
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <Backdrop
            $variant={variant}
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            initial={{ opacity: 0, y: isPreview ? 0 : 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: isPreview ? 0 : 24 }}
            transition={{ duration: 0.2 }}
            className={[
              'fixed inset-0 z-[20001]',
              'w-screen h-[100dvh]',
              isPreview ? 'overflow-hidden p-0' : 'overflow-y-auto overscroll-contain p-4 sm:p-0',
            ].join(' ')}
          >
            <div
              className={[
                'w-full h-full',
                isPreview ? 'overflow-hidden' : 'min-h-full flex items-start sm:items-center justify-center',
              ].join(' ')}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className={isPreview ? 'w-full h-full' : 'w-full sm:w-auto'}
              >
                {children}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}

const Backdrop = styled(motion.div) <{ $variant: 'default' | 'preview' }>`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100dvh;
  z-index: 20000;

  ${(p) =>
    p.$variant === 'preview'
      ? `
    background: #000;
    backdrop-filter: none;
    background-image: none;
  `
      : `
    background-color: rgba(0, 0, 0, 0.75);
    backdrop-filter: blur(20px);
    background-size: 10px 10px;
    background-image: radial-gradient(transparent 3px, rgb(0, 0, 0, 0.5) 3px);
  `}
`;